import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { X, ArrowLeft, ArrowRight } from "lucide-react";

export default function ProjectLightbox({ project, idx, setIdx, onClose }) {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-sm overflow-y-auto"
            data-testid="project-lightbox"
            onClick={onClose}
        >
            <button
                onClick={onClose}
                aria-label="Close"
                data-testid="lightbox-close"
                className="fixed top-4 right-4 md:top-8 md:right-8 text-bone hover:text-clay z-[110] bg-ink/60 hover:bg-clay/80 backdrop-blur-md rounded-full p-2 transition-colors"
            >
                <X size={24} />
            </button>

            <div
                onClick={(e) => e.stopPropagation()}
                className="min-h-full w-full max-w-6xl mx-auto px-4 md:px-10 py-16 md:py-20"
            >
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-10 items-start">
                    {/* Image */}
                    <div className="lg:col-span-3 relative">
                        <div className="aspect-[4/3] overflow-hidden bg-char">
                            <img
                                src={project.gallery[idx]}
                                alt={`${project.name} - ${idx + 1}`}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                            <div className="text-xs text-bone/60 tracking-[0.2em] uppercase">
                                {idx + 1} / {project.gallery.length}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setIdx((i) => (i - 1 + project.gallery.length) % project.gallery.length)}
                                    data-testid="lightbox-prev"
                                    className="p-2 border border-bone/20 text-bone hover:bg-bone hover:text-ink transition-colors"
                                >
                                    <ArrowLeft size={16} />
                                </button>
                                <button
                                    onClick={() => setIdx((i) => (i + 1) % project.gallery.length)}
                                    data-testid="lightbox-next"
                                    className="p-2 border border-bone/20 text-bone hover:bg-bone hover:text-ink transition-colors"
                                >
                                    <ArrowRight size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Thumbs */}
                        <div className="mt-3 grid grid-cols-4 gap-2">
                            {project.gallery.map((g, i) => (
                                <button
                                    key={g}
                                    onClick={() => setIdx(i)}
                                    className={`aspect-[4/3] overflow-hidden ${i === idx ? "ring-2 ring-clay" : "opacity-60 hover:opacity-100"}`}
                                >
                                    <img src={g} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Info */}
                    <div className="lg:col-span-2 text-bone">
                        <div className="overline text-clay mb-4">{project.type}</div>
                        <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-tight">
                            {project.suburb}
                        </h2>
                        <p className="mt-6 text-bone/80 leading-relaxed">
                            {project.description}
                        </p>
                    </div>
                </div>

                {/* Watch Video — placed beneath the gallery */}
                {project.video && project.video.provider === "vimeo" && (
                    <div className="mt-12 md:mt-20">
                        <div className="flex items-center gap-4 mb-6 md:mb-8">
                            <span className="inline-block w-10 h-px bg-clay" />
                            <h3 className="overline text-clay">Watch Video</h3>
                        </div>
                        <div className="relative w-full overflow-hidden bg-char" style={{ paddingBottom: "56.25%" }}>
                            <iframe
                                src={`https://player.vimeo.com/video/${project.video.id}?autoplay=0&loop=1&muted=0&dnt=1&title=0&byline=0&portrait=0`}
                                title={`${project.name} — video tour`}
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowFullScreen
                                className="absolute inset-0 w-full h-full"
                                style={{ border: 0 }}
                                data-testid="lightbox-video"
                            />
                        </div>
                    </div>
                )}

                {/* Contact CTA */}
                <div className="mt-12 md:mt-20 border-t border-bone/15 pt-10 md:pt-14 text-center">
                    <div className="overline text-clay mb-4">Start your project</div>
                    <h3 className="font-display text-bone text-2xl md:text-4xl font-light tracking-tight">
                        Like what you see? <span className="italic text-clay">Let's talk.</span>
                    </h3>
                    <Link
                        to="/contact"
                        data-testid="lightbox-contact-cta"
                        className="btn-primary mt-8 inline-flex"
                    >
                        Contact Us
                    </Link>
                </div>
            </div>
        </motion.div>
    );
}
